import { updateValuesWrapper } from "@repo/dynamicEditor/src/helpers/updatePropertiesHooks";
import { PushProcessingNotification } from "@repo/helpers";
import { FilterItem, KeyedArray, keyPair } from "@repo/models";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo } from "react";

import * as Queries from "../../api/queries";
import { useRepApi } from "../../api/repApi";
import { useRepDisciplineQueries } from "../../api/repDisciplineApi";
import { EditableItemDynamicTab } from "../../components/dynamicEditor/dynamicTab/editableItem";
import { ITemplateData } from "../../components/dynamicEditor/templateDataHook";
import {
	RepDeactivationProvider,
	RepProvider,
	useRepDeactivationDetailsContext,
} from "../../contexts/editableItemContext";
import { PendingChangesProvider } from "../../contexts/pendingChangesContext";
import { useSettingsQuery } from "../../helpers/queryHooks";
import { Rep } from "../../models";
import { RepDeactivationDetails } from "../../models/repDeactivationDetails";
import { modals } from "../../string";

interface IState extends ITemplateData {
	repDeactivationDetails: RepDeactivationDetails;
}

const SOURCE = [
	{ id: 1, value: "Complaint" },
	{ id: 2, value: "Onboarding" },
	{ id: 3, value: "Internal Review" },
];

const TYPES = [
	{ id: 1, value: "Deactivation" },
	{ id: 2, value: "Suspension" },
	{ id: 3, value: "Terminated" },
	{ id: 4, value: "Blocklist" },
	{ id: 5, value: "Denied" },
];

function useRepDisciplinesLists() {
	const [settings] = useSettingsQuery();
	const { useTrainingsQuery } = useRepDisciplineQueries();
	const { data: trainings, loaded: trainingsLoaded } = useTrainingsQuery(settings?.mindflashEnabled);

	const activeLists = useMemo(() => {
		return {
			types: TYPES,
			source: SOURCE,
			trainingLists: getItems(trainings, trainingsLoaded),
		};
	}, [trainings]);

	return [activeLists];

	function getItems(allItems: KeyedArray<FilterItem>, loaded: boolean, forceInclude?: FilterItem) {
		if (!allItems) return [] as keyPair[];
		if (forceInclude) allItems.set(forceInclude);
		return allItems.map((c) => {
			return { id: c.id, value: c.name } as keyPair;
		});
	}
}

interface Props {
	rep: Rep;
	repSegmentId: number | undefined;
	close: () => void;
	action: string;
}

export function Deactivation(props: Props) {
	return (
		<PendingChangesProvider>
			<RepProvider item={props.rep}>
				<RepDeactivationProvider>
					<DeactivationInternal {...props} />
				</RepDeactivationProvider>
			</RepProvider>
		</PendingChangesProvider>
	);
}

export function DeactivationInternal(props: Props) {
	const { rep, repSegmentId, action } = props;
	const repId = rep.repId;
	const context = useRepDeactivationDetailsContext();
	const repDeactivationDetails = context.item;
	const [repDisciplinesLists] = useRepDisciplinesLists();
	const { updateStatus } = useRepApi();

	const queryClient = useQueryClient();

	useEffect(() => {
		if (context.item) return;

		context.overwriteItem({
			RepresentativeId: repId,
			Action: action,
		} as RepDeactivationDetails);
	}, []);

	const templateData = useMemo(
		() => ({
			repDeactivationDetails,
			repDisciplinesLists,
		}),
		[repDeactivationDetails, repDisciplinesLists],
	);

	const save = useCallback(async () => {
		const notify = PushProcessingNotification("Saving Discipline");
		try {
			if (action === "DeactivateAll") {
				await updateStatus(action, rep.repId, undefined, repDeactivationDetails);
			} else if (repSegmentId) {
				await updateStatus(action, undefined, repSegmentId, repDeactivationDetails);
			} else {
				throw new Error("Failed to update Discipline");
			}

			await queryClient.invalidateQueries({ queryKey: [Queries.GROUP.REP] });
			notify.success("Saved!");
			props.close();
			return undefined;
		} catch (ex) {
			notify.error("Failed to save");
			props.close();
			throw ex;
		}
	}, [repDeactivationDetails, updateStatus, action, repSegmentId, rep.repId]);

	const updateValues = useMemo(() => updateValuesWrapper(applyBusinessLogic), [applyBusinessLogic]);

	async function updateValueInternal(id: string, value: any, currentState: IState, parentComponent?: string) {
		const { newState } = await updateValues(id, value, currentState, parentComponent);
		return newState ?? currentState;
	}

	async function applyBusinessLogic(id: string, value: any, newState: IState) {
		async function update(name: string, value: any) {
			newState = await updateValueInternal(name, value, newState);
		}
		//Custom logic
		switch (id) {
			case "repDeactivationDetails.DisciplineSourceId":
				update("repDeactivationDetails.DisciplineSource", value);
				break;
			default:
				break;
		}

		return newState as IState;
	}

	return (
		<EditableItemDynamicTab
			itemContext={context}
			pageName={modals.deactivationForm}
			otherTemplateData={templateData}
			itemProp="repDeactivationDetails"
			updateValues={updateValues}
			save={save}
			loaded
		/>
	);
}
